import React, { useEffect, useState } from "react";
import { Box, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import { Button, Grid } from "@material-ui/core";
import Paper from "@material-ui/core/Paper";
import { GridToolbar, DataGrid } from "@material-ui/data-grid";
import Skeleton from "@mui/material/Skeleton";
import {
    makeGETRequestOnUserService,
    makePUTRequestOnUserService,
    downloadUserServiceFileUsingGET,
} from "../../Api/api";
import { User } from "./DownloadPermissions";
import EditIcon from "@mui/icons-material/Edit";
import IconButton from "@mui/material/IconButton";
import FullScreenDialog from "../../components/Dialog/FullScreenDialog";
import CKYCUpdate from "../../components/Users/CKYCUpdate";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DownloadForOfflineIcon from '@mui/icons-material/DownloadForOffline';
import DashboardCard from "../../components/GenericDashboardCard/DashboardCard";
import InfoIcon from '@mui/icons-material/Info';
import GroupIcon from "@mui/icons-material/Group";
import PersonIcon from "@mui/icons-material/Person";
import toastr from "toastr";
import { downloadPDFFile } from "../../Helpers/functions";


const CKYCDashboard = () => {

    const [users, setUsers] = useState<User[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [openUploadModal, setOpenUploadModal] = useState(false);
    const [selectedRowId, setSelectedRowId] = useState<{
        ckycUUID: string | null;
        user_type: string | undefined;
    }>({ ckycUUID: null, user_type: undefined });

    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    // const [downloadingDoc, setDownloadingDoc] = useState(false);
    const [downloadingDoc, setDownloadingDoc] = useState<string | null>(null);
    const [totalCKYC, setTotalCKYC] = useState(0);
    const [totalPending, setTotalPending] = useState(0);
    const [totalApproved, setTotalApproved] = useState(0);
    const [totalDelayed, setTotalDelayed] = useState(0);
    const [selected, setSelected] = useState({
        ckycUUID: "",
        file: null as File | null,
        userData: null as any,
    });

    const buildUrl = (endpoint: string, user_type?: string, uuid?: string) => {
        if (!user_type) {
            throw new Error("user_type is missing");
        }

        if (!uuid) {
            throw new Error("uuid is missing");
        }

        return user_type === "smest"
            ? `/kyc/ckyc_detail/${endpoint}/${uuid}/`
            : `/partneruser/ckyc_detail/${endpoint}/${user_type}/${uuid}/`;
    };

    const fetchUsers = () => {
        setIsLoading(true);
        makeGETRequestOnUserService("/order_management_v2/ckyc_user_list/")
            .then((resp) => {
                if (resp.status === 200) {
                    const data = resp.data || [];
                    setUsers(data);
                    setTotalCKYC(data.length);
                    setTotalPending(
                        data.filter((u: any) => {
                            const status = u.ckyc?.internalStatus?.toLowerCase() || "";
                            return status === "pending" || !status || status === "none";
                        }).length
                    );
                    setTotalApproved(
                        data.filter((u: any) =>
                            u.ckyc?.internalStatus?.toLowerCase() === "approved"
                        ).length
                    );
                    setTotalDelayed(
                        data.filter((u: any) => u.kpi === "Delayed").length
                    );
                }
            })
            .catch(() => toastr.error("Failed to fetch user list."))
            .finally(() => setIsLoading(false));
    };

    useEffect(() => {
        fetchUsers();
    }, []);


    const formatISTDateTime = (value: any) => {
        if (!value) return "";

        return new Date(value).toLocaleString("en-IN", {
            timeZone: "Asia/Kolkata",
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    const handleUploadClick = (ckycUUID: string, user_type: string) => {
        setSelectedRowId({ ckycUUID, user_type: user_type });
        setOpenUploadModal(true);
    };
    const handleDownloadClick = async (ckycUUID: string, userName: string, user_type: string) => {
        try {
            setDownloadingDoc(ckycUUID);
            const url = buildUrl("download_document", user_type, ckycUUID);
            const response = await downloadUserServiceFileUsingGET(
                url
            );
            if (response && response.status === 200) {
                toastr.success("cKYC Document Downloaded Successfully");
                downloadPDFFile(response.data, `${userName}_ckyc_document.pdf`);
            }
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : "Failed to download file";
            toastr.error(errorMessage);
        } finally {
            setDownloadingDoc(null);
        }
    };

    const getFullName = (row: any) => {
        const first = row.firstName || row.first_name || "";
        const last = row.lastName || row.last_name || "";
        return `${first} ${last}`.trim();
    };

    const dataGridCells = [
        {
            field: "clientName",
            headerName: "Client Name",
            width: 200,
            valueGetter: (params: any) => getFullName(params.row),
        },
        {
            field: "smestID",
            headerName: "SMEST ID",
            width: 150,
            valueGetter: (params: any) => params.row?.smestID || params.row?.smest_id || "",
        },
        {
            field: "kycApprovedDate",
            headerName: "KYC Approved Date",
            width: 220,
            valueGetter: (params: any) =>
                formatISTDateTime(params.row.kyc_markedcompleted_at),
        },
        {
            field: "ckycDeadline",
            headerName: "cKYC Deadline Date",
            width: 220,
            valueGetter: (params: any) =>
                params.row.ckyc?.deadlineDateTime
                    ? formatISTDateTime(params.row.ckyc.deadlineDateTime)
                    : "",
        },
        {
            field: "type",
            headerName: "Type",
            width: 150,
            valueGetter: (params: any) => params.row?.type?.toUpperCase() || "",
        },
        {
            field: "action",
            headerName: "Action",
            width: 150,
            valueGetter: (params: any) => params.row.ckyc?.action || "",
        },
        {
            field: "duplicates",
            headerName: "Duplicates",
            width: 150,
            renderCell: (params: any) => {
                const val = params.row.ckyc?.duplicates;
                if (val === "Duplication Found") {
                    return (
                        <span style={{ color: "red" }}>
                            Yes
                        </span>
                    );
                }
                if (val === "No Duplicate cKYC") {
                    return (
                        <span style={{ color: "black" }}>
                            No
                        </span>
                    );
                }
                return "";
            },
        },
        {
            field: "document",
            headerName: "Document",
            width: 150,
            renderCell: (params: any) => {
                const ckycUUID = params.row.ckyc?.uuid;
                const user_type = params.row?.type;

                const userName = getFullName(params.row)
                const hasDocument = params.row.ckyc?.ckycDocument;
                const isDownloading = downloadingDoc === ckycUUID;
                return (
                    <Box sx={{ display: "flex", gap: 4, justifyContent: "center" }}>
                        <IconButton size="small" color="primary" onClick={() => handleUploadClick(ckycUUID, user_type)}>
                            <CloudUploadIcon />
                        </IconButton>
                        {hasDocument && (
                            <IconButton
                                size="small"
                                sx={{ color: isDownloading ? "gray" : "green" }}
                                onClick={() => handleDownloadClick(ckycUUID, userName, user_type)}
                                disabled={isDownloading}
                            >
                                {isDownloading ? <CircularProgress size={20} /> : <DownloadForOfflineIcon />}
                            </IconButton>
                        )}
                    </Box>
                );
            },
        },
        {
            field: "stateAction",
            headerName: "State Action",
            width: 170,
            renderCell: (params: any) => {
                const state = params.row.addressState?.toLowerCase();
                const isSikkim = state === "sikkim"
                return (
                    <span style={{ color: isSikkim ? "red" : "black" }}>
                        {isSikkim ? "Yes" : "No"}
                    </span>
                );
            },
        },
        {
            field: "ckycStatus",
            headerName: "cKYC Status",
            width: 200,
            renderCell: (params: any) => {
                const status = params.row.ckyc?.internalStatus || "";
                let color = "black";
                if (status.toLowerCase() === "approved") color = "green";
                else if (status.toLowerCase() === "pending") color = "red";
                return (
                    <span style={{ color }}>
                        {status}
                    </span>
                );
            },
        },
        {
            field: "kpi",
            headerName: "KPI",
            width: 130,
            renderCell: (params: any) => {
                const value = params.value;
                let color = "black";
                if (value === "In Process") color = "orange";
                else if (value === "On Time") color = "green";
                else if (value === "Delayed") color = "red";
                return (
                    <span style={{ color }}>
                        {value || ""}
                    </span>
                );
            },
        },
        {
            field: "update",
            headerName: "Update",
            width: 130,
            renderCell: (params: any) => (
                <IconButton
                    size="small"
                    onClick={async () => {
                        const row = params.row;

                        setSelected((p) => ({
                            ...p,
                            userData: {
                                userUUID: row.uuid ?? "",
                                ckycUUID: row.ckyc?.uuid ?? "",
                                firstName: row.firstName || row.first_name || "",
                                lastName: row.lastName || row.last_name || "",
                                smestID: row.smestID || row.smest_id || "",
                                addressState: row.addressState || "",
                                kycMarkedCompletedAt: formatISTDateTime(row.kyc_markedcompleted_at) ?? "",
                                deadlineDate: formatISTDateTime(row.ckyc?.deadlineDateTime) ?? "",
                                user_type: row.type || "",
                            }
                        }));

                        setOpenDialog(true);
                    }}
                >
                    <EditIcon />
                </IconButton>
            ),
        },
    ];

    const handleUpload = async () => {
        const { ckycUUID, user_type } = selectedRowId;
        if (!ckycUUID || !selectedFile) {
            toastr.error("Please select a file");
            return;
        }

        try {
            setIsUploading(true);

            const formData = new FormData();
            formData.append("file", selectedFile);
            const url = buildUrl("upload_document", user_type, ckycUUID);
            const resp = await makePUTRequestOnUserService(url, formData);

            if (resp.status === 200) {
                toastr.success("Document uploaded successfully");
                fetchUsers();
            } else {
                toastr.error("Failed to upload document");
            }
        } catch (err) {
            console.error(err);
            toastr.error("Upload failed");
        } finally {
            setIsUploading(false);
            setOpenUploadModal(false);
            setSelectedFile(null);
        }
    };

    return (
        <>
            <Grid className="QuotesDataGrid">
                <Paper>
                    <Grid style={{ margin: "20px 0", padding: "0 50px" }}>
                        <Grid container alignItems="center" spacing={3}>
                            <Grid item xs>
                                <div style={{ fontSize: "36px", float: "left" }}>cKYC Central Dashboard</div>
                            </Grid>
                        </Grid>

                        <Grid container spacing={2}>
                            {!isLoading ? (
                                <>
                                    <Grid item xs={3}>
                                        <DashboardCard title="Total cKYC" primaryValue={totalCKYC} icon={<InfoIcon />} />
                                    </Grid>
                                    <Grid item xs={3}>
                                        <DashboardCard title="Total Pending cKYC" primaryValue={totalPending} icon={<InfoIcon />} />
                                    </Grid>
                                    <Grid item xs={3}>
                                        <DashboardCard title="Total Completed cKYC" primaryValue={totalApproved} icon={<PersonIcon />} />
                                    </Grid>
                                    <Grid item xs={3}>
                                        <DashboardCard title="cKYC Delayed" primaryValue={totalDelayed} icon={<GroupIcon />} />
                                    </Grid>
                                </>
                            ) : (
                                <>
                                    {[1, 2, 3, 4].map((i) => (
                                        <Grid item xs={3} key={i}>
                                            <Skeleton
                                                variant="rectangular"
                                                width="100%"
                                                height={120}
                                                animation="wave"
                                                style={{ borderRadius: 12 }}
                                            />
                                        </Grid>
                                    ))}
                                </>
                            )}
                        </Grid>

                    </Grid>

                    <Box sx={{ height: "100vh", width: "100%", px: [2, 9], "& .MuiDataGrid-columnHeaderTitle": { fontWeight: "bold" } }}>
                        {!isLoading ? (
                            <DataGrid
                                pagination
                                components={{ Toolbar: GridToolbar }}
                                rows={users}
                                columns={dataGridCells}
                                getRowId={(row) => row.uuid}
                            />
                        ) : (
                            <Box>{[1, 2, 3, 4, 5].map((i) => <Skeleton key={i} height={50} animation="wave" />)}</Box>
                        )}
                    </Box>
                </Paper>
            </Grid>

            <Dialog open={openUploadModal} onClose={() => setOpenUploadModal(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Upload Document</DialogTitle>

                <DialogContent dividers>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        <input
                            type="file"
                            id="ckyc-upload"
                            accept=".pdf,.xls,.xlsx"
                            style={{ display: "none" }}
                            onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                        />

                        <label htmlFor="ckyc-upload">
                            <Button variant="outlined" component="span" fullWidth startIcon={<CloudUploadIcon />}>
                                {selectedFile ? selectedFile.name : "Choose PDF File"}
                            </Button>
                        </label>
                    </Box>
                </DialogContent>

                <DialogActions>
                    <Button onClick={() => setOpenUploadModal(false)}>Cancel</Button>

                    <Button
                        variant="contained"
                        color="primary"
                        disabled={isUploading}
                        onClick={handleUpload}
                    >
                        {isUploading ? "Loading..." : "Upload"}
                    </Button>
                </DialogActions>
            </Dialog>

            <FullScreenDialog
                show={openDialog}
                heading="Update CKYC"
                handleClose={() => setOpenDialog(false)}
                showSaveButton={true}
                saveButtonText="Save"
                onSumitSaveButton={() => {
                    setOpenDialog(false);
                    fetchUsers();
                }}
                inputProps={<CKYCUpdate data={selected.userData} />}
            />
        </>
    );
};

export default CKYCDashboard;
